import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GlassWater, Minus, Plus, Settings2 } from 'lucide-react';

const GLASS_ML = 250;

/** Suivi de l'eau : grille de verres cliquables (250ml/verre), objectif éditable. */
export const WaterTracker = ({ waterMl, goalMl, onAdd, onSetGoal, en }) => {
  const [editingGoal, setEditingGoal] = useState(false);
  const [draftGoal, setDraftGoal] = useState((goalMl || 2000) / 1000);
  const totalGlasses = Math.max(1, Math.round((goalMl || 2000) / GLASS_ML));
  const filled = Math.floor((waterMl || 0) / GLASS_ML);

  useEffect(() => { if (!editingGoal) setDraftGoal((goalMl || 2000) / 1000); }, [goalMl, editingGoal]);

  const saveGoal = () => {
    onSetGoal(Math.max(500, Math.round(draftGoal * 1000)));
    setEditingGoal(false);
  };

  return (
    <div className="rounded-3xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-5" data-testid="water-tracker-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <GlassWater className="w-5 h-5 text-sky-400" />
          <h3 className="font-bold text-white">{en ? 'Water intake' : "Suivi de l'eau"}</h3>
        </div>
        <button onClick={() => setEditingGoal((v) => !v)} className="p-1.5 rounded-lg text-zinc-500 hover:text-white hover:bg-white/10 transition-colors" data-testid="water-goal-edit-btn">
          <Settings2 className="w-4 h-4" />
        </button>
      </div>

      {editingGoal ? (
        <div className="flex items-center gap-2 mb-4" data-testid="water-goal-editor">
          <button onClick={() => setDraftGoal((v) => Math.max(0.5, v - 0.25))} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white"><Minus className="w-3.5 h-3.5" /></button>
          <span className="text-sm font-bold text-white tabular-nums w-16 text-center">{draftGoal.toFixed(2)} L</span>
          <button onClick={() => setDraftGoal((v) => v + 0.25)} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white"><Plus className="w-3.5 h-3.5" /></button>
          <button onClick={saveGoal} className="ml-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-primary to-pink-500 text-white text-xs font-bold" data-testid="water-goal-save-btn">{en ? 'Save' : 'Valider'}</button>
        </div>
      ) : (
        <p className="text-sm text-zinc-400 mb-4">
          <span className="text-white font-bold tabular-nums">{((waterMl || 0) / 1000).toFixed(2)} L</span> / {((goalMl || 2000) / 1000).toFixed(1)} L
        </p>
      )}

      <div className="grid grid-cols-5 sm:grid-cols-8 gap-2.5">
        {Array.from({ length: totalGlasses }).map((_, i) => {
          const active = i < filled;
          const isNext = i === filled;
          return (
            <motion.button
              key={i} whileTap={{ scale: 0.85 }}
              onClick={() => onAdd(active ? -GLASS_ML : GLASS_ML)}
              className={`relative aspect-square rounded-xl flex items-center justify-center transition-colors ${active ? 'bg-sky-400/20 text-sky-400 border border-sky-400/40' : 'bg-white/5 text-zinc-600 border border-white/5 hover:border-white/15'}`}
              data-testid={`water-glass-button-${i}`}
              title={`${(i + 1) * GLASS_ML} ml`}
            >
              <GlassWater className="w-5 h-5" />
              {isNext && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-sky-400 text-black text-[10px] font-black flex items-center justify-center">+</span>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default WaterTracker;
